// ============================================
// ./src/contexts/LocationHistoryContext/LocationHistoryContext.tsx
// Manages GPS location requests with a geocode result cache.
//
// Design principles:
// - GPS coordinates are always fetched fresh (maximumAge: 0) — free and fast
// - Reverse geocode API results are cached (max 10 entries) to avoid
//   redundant API calls. Cache is keyed by proximity — if new coords are
//   within threshold of a cached entry that has an address, reuse it.
// - No location caching — currentLocation always reflects the last request,
//   never returned as a shortcut for a new request.
// ============================================

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";

import { GeoCoords } from "../hooks/useGeolocation";
import { useGoogleMapsAutocomplete } from "../hooks/useGoogleMapsAutocomplete";
import {
  areCoordinatesNear,
  isValidCoordinate,
  DEFAULT_DISTANCE_THRESHOLD_M,
} from "@em/utils";

import {
  CachedLocation,
  SerializedLocationCache,
  RequestLocationOptions,
  FreshnessCheckOptions,
  LocationHistoryContextValue,
  LocationHistoryProviderConfig,
  LocationHistoryProviderProps,
} from "./types";

// ============================================
// Constants
// ============================================

const DEFAULT_CONFIG: Required<LocationHistoryProviderConfig> = {
  maxHistorySize: 10,
  deduplicationDistanceM: DEFAULT_DISTANCE_THRESHOLD_M,
  defaultMaxAgeMs: 0,                          // Unused — kept for type compat
  defaultMaxDistanceM: DEFAULT_DISTANCE_THRESHOLD_M,
  persistToStorage: true,
  storageKey: "location_geocode_cache",        // Renamed to reflect new purpose
};

const CACHE_VERSION = 1;

// Proximity threshold for geocode cache lookup:
// If new coords are within this distance of a cached geocode result,
// reuse the cached address instead of calling the API again.
const GEOCODE_CACHE_THRESHOLD_M = 100; // 100 meters

// ============================================
// Storage Helpers
// Only the geocode result cache (recentLocations) is persisted.
// currentLocation is not persisted — it always reflects the last
// live request and is meaningless across page reloads.
// ============================================

function loadFromStorage(storageKey: string): CachedLocation[] {
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return [];

    const parsed: SerializedLocationCache = JSON.parse(stored);

    if (parsed.version !== CACHE_VERSION) {
      sessionStorage.removeItem(storageKey);
      return [];
    }

    return parsed.history ?? [];
  } catch (error) {
    console.warn("[LocationHistoryContext] Failed to load from storage:", error);
    return [];
  }
}

function saveToStorage(storageKey: string, history: CachedLocation[]): void {
  try {
    const data: SerializedLocationCache = {
      current: null,
      history,
      version: CACHE_VERSION,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.warn("[LocationHistoryContext] Failed to save to storage:", error);
  }
}

function clearStorage(storageKey: string): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch (error) {
    console.warn("[LocationHistoryContext] Failed to clear storage:", error);
  }
}

// ============================================
// Helper Functions
// ============================================

// FIX: Use crypto.randomUUID() instead of Date.now() + Math.random().
// The previous implementation could produce duplicate IDs for rapid calls
// within the same millisecond tick.
function generateCacheId(): string {
  return crypto.randomUUID();
}

/**
 * Add a geocode result to the cache.
 * If coords are near an existing entry, update it in place.
 * Otherwise prepend and trim to maxSize.
 */
function addToGeocodeCache(
  cache: CachedLocation[],
  newEntry: CachedLocation,
  maxSize: number,
  thresholdM: number
): CachedLocation[] {
  const nearbyIndex = cache.findIndex((existing) =>
    areCoordinatesNear(existing.coords, newEntry.coords, thresholdM)
  );

  if (nearbyIndex !== -1) {
    // Update existing entry in place
    const updated = [...cache];
    updated[nearbyIndex] = newEntry;
    return updated;
  }

  // Prepend new entry and trim
  return [newEntry, ...cache].slice(0, maxSize);
}

// ============================================
// Context
// ============================================

const defaultContextValue: LocationHistoryContextValue = {
  isSupported: false,
  isLocating: false,
  isReverseGeocoding: false,
  isLoading: false,
  error: null,
  permissionDenied: false,
  currentLocation: null,
  recentLocations: [],
  requestLocation: async () => null,
  getLocationIfFresh: () => null,
  hasUserMoved: () => false,
  findNearbyCache: () => null,
  clearHistory: () => { },
};

const LocationHistoryContext =
  createContext<LocationHistoryContextValue>(defaultContextValue);

// ============================================
// Provider Component
// ============================================

export const LocationHistoryProvider: React.FC<LocationHistoryProviderProps> = ({
  children,
  config = {},
}) => {
  const cfg = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...config }),
    [config]
  );

  // FIX: Extract primitives used directly inside requestLocation instead of
  // depending on the whole cfg object. cfg is a new reference every render
  // (useMemo with an object spread), so listing it in a useCallback dep array
  // causes that callback to recreate on every render, which can trigger
  // infinite loops if any consumer effect depends on requestLocation.
  //
  // cfg.persistToStorage / cfg.storageKey are only used in clearHistory and
  // the saveToStorage effect — not inside requestLocation — so they don't
  // belong in requestLocation's dep array at all.
  const maxHistorySize = cfg.maxHistorySize;

  const isSupported = useMemo(
    () => typeof window !== "undefined" && "geolocation" in navigator,
    []
  );

  const { reverseGeocode } = useGoogleMapsAutocomplete();

  // currentLocation: result of the most recent requestLocation call.
  // Never used as a cache shortcut — always reflects a live GPS result.
  const [currentLocation, setCurrentLocation] = useState<CachedLocation | null>(null);

  // geocodeCache: reverse geocode API results keyed by proximity.
  // Persisted to sessionStorage so results survive drawer open/close.
  const [geocodeCache, setGeocodeCache] = useState<CachedLocation[]>(() => {
    if (!cfg.persistToStorage) return [];
    return loadFromStorage(cfg.storageKey);
  });

  const [isLocating, setIsLocating] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Persist geocode cache to sessionStorage whenever it changes
  useEffect(() => {
    if (cfg.persistToStorage) {
      saveToStorage(cfg.storageKey, geocodeCache);
    }
  }, [geocodeCache, cfg.persistToStorage, cfg.storageKey]);

  // ============================================
  // GPS
  // ============================================

  /**
   * Get fresh coordinates from the browser.
   * maximumAge: 0 — browser must not return a cached position.
   */
  const getBrowserLocation = useCallback(
    async (
      highAccuracy: boolean = false
    ): Promise<{ coords: GeoCoords; accuracy: number } | null> => {
      if (!isSupported) {
        setError("Geolocation is not supported by your browser");
        return null;
      }

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (!isMountedRef.current) {
              resolve(null);
              return;
            }
            resolve({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              },
              accuracy: position.coords.accuracy,
            });
          },
          (err) => {
            if (!isMountedRef.current) {
              resolve(null);
              return;
            }
            const errorMessages: Record<number, string> = {
              1: "Location permission was denied",
              2: "Location information is unavailable",
              3: "Location request timed out",
            };
            setError(errorMessages[err.code] || "Failed to get location");
            setPermissionDenied(err.code === 1);
            resolve(null);
          },
          {
            enableHighAccuracy: highAccuracy,
            maximumAge: 0,    // Always fresh — never use browser's cached position
            timeout: 10000,
          }
        );
      });
    },
    [isSupported]
  );

  /**
   * Check geolocation permission state using Permissions API.
   */
  const checkPermissionState = useCallback(async (): Promise<
    "granted" | "denied" | "prompt" | "unknown"
  > => {
    if (!navigator.permissions) return "unknown";
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      return status.state;
    } catch {
      return "unknown";
    }
  }, []);

  // ============================================
  // Geocode Cache Lookup
  // ============================================

  /**
   * Find a cached geocode result near the given coordinates.
   * Only returns entries that have an address (i.e., were reverse geocoded).
   */
  const findNearbyCache = useCallback(
    (coords: GeoCoords, thresholdM: number = cfg.defaultMaxDistanceM): CachedLocation | null => {
      if (!isValidCoordinate(coords)) return null;

      for (const cached of geocodeCache) {
        if (
          cached.address &&
          areCoordinatesNear(cached.coords, coords, thresholdM)
        ) {
          return cached;
        }
      }

      return null;
    },
    [geocodeCache, cfg.defaultMaxDistanceM]
  );

  // ============================================
  // requestLocation
  // ============================================

  /**
   * Request the user's current location.
   *
   * GPS coordinates are always fetched fresh — no position caching.
   *
   * When reverseGeocode: true, the geocode cache is checked first.
   * If new coords are within GEOCODE_CACHE_THRESHOLD_M of a cached
   * geocode result, the cached address is returned without an API call.
   * Otherwise the API is called and the result is stored in the cache.
   *
   * allowPrompt: controls whether a permission prompt may be shown.
   * Set to true only when the user explicitly clicks "Use my location".
   */
  const requestLocation = useCallback(
    async (
      options: RequestLocationOptions = {}
    ): Promise<CachedLocation | null> => {
      const {
        reverseGeocode: shouldReverseGeocode = false,
        highAccuracy = false,
        allowPrompt = false,
      } = options;

      // Guard: check permission before potentially prompting
      if (!allowPrompt) {
        const permissionState = await checkPermissionState();

        if (permissionState === "denied") {
          setPermissionDenied(true);
          return null;
        }

        if (permissionState === "prompt") {
          // Don't auto-trigger prompt — wait for explicit user action
          return null;
        }
      }

      // Step 1: Get fresh GPS coordinates
      setIsLocating(true);
      setError(null);

      const browserResult = await getBrowserLocation(highAccuracy);

      if (!browserResult || !isMountedRef.current) {
        setIsLocating(false);
        return null;
      }

      setIsLocating(false);

      // Step 2: Build base location entry from fresh GPS coords
      let newLocation: CachedLocation = {
        id: generateCacheId(),
        coords: browserResult.coords,
        accuracy: browserResult.accuracy,
        timestamp: Date.now(),
      };

      // Step 3: Resolve address if requested
      if (shouldReverseGeocode) {
        // Check geocode cache first — avoid API call if we've recently
        // geocoded coords near this position.
        const cached = findNearbyCache(
          browserResult.coords,
          GEOCODE_CACHE_THRESHOLD_M
        );

        if (cached?.address) {
          // Cache hit — reuse the cached address without updating the cache entry.
          //
          // Intentionally do NOT call setGeocodeCache here. The cache anchor
          // must stay pinned to the coords that were originally reverse geocoded.
          // If we updated the anchor to the fresh GPS fix on every hit, the entry
          // would drift up to GEOCODE_CACHE_THRESHOLD_M per request — eventually
          // placing the anchor far from the address it actually describes.
          newLocation = {
            ...newLocation,
            address: cached.address,
          };
        } else {
          // Cache miss — call the reverse geocode API
          setIsReverseGeocoding(true);

          try {
            const addressData = await reverseGeocode(browserResult.coords);

            if (addressData && isMountedRef.current) {
              newLocation = { ...newLocation, address: addressData };

              // Store in geocode cache for future requests at this location
              setGeocodeCache((prev) =>
                addToGeocodeCache(
                  prev,
                  newLocation,
                  maxHistorySize,           // FIX: was cfg.maxHistorySize (cfg object in dep array)
                  GEOCODE_CACHE_THRESHOLD_M
                )
              );
            }
          } catch (err) {
            console.warn(
              "[LocationHistoryContext] Reverse geocoding failed:",
              err
            );
            // Continue without address — coords are still useful
          } finally {
            if (isMountedRef.current) {
              setIsReverseGeocoding(false);
            }
          }
        }
      }

      if (!isMountedRef.current) return null;

      // Update currentLocation with the fresh result
      setCurrentLocation(newLocation);
      setPermissionDenied(false);

      return newLocation;
    },
    [
      checkPermissionState,
      getBrowserLocation,
      findNearbyCache,
      reverseGeocode,
      maxHistorySize,           // FIX: was cfg (entire object) — caused recreation every render
    ]
  );

  // ============================================
  // Remaining Methods
  // ============================================

  /**
   * getLocationIfFresh — kept for API compatibility with consumers
   * that still call it (e.g. useAutoLocation).
   * Always returns null now since we no longer cache positions.
   * This causes useAutoLocation to always proceed to a fresh GPS request,
   * which is exactly what we want.
   */
  const getLocationIfFresh = useCallback(
    (_options: FreshnessCheckOptions = {}): CachedLocation | null => {
      return null;
    },
    []
  );

  /**
   * hasUserMoved — compares new coords against currentLocation.
   * Still useful for callers that want to know if position changed
   * since the last requestLocation call.
   */
  const hasUserMoved = useCallback(
    (newCoords: GeoCoords, thresholdM: number = cfg.defaultMaxDistanceM): boolean => {
      if (!currentLocation || !isValidCoordinate(newCoords)) return true;
      return !areCoordinatesNear(newCoords, currentLocation.coords, thresholdM);
    },
    [currentLocation, cfg.defaultMaxDistanceM]
  );

  /**
   * clearHistory — clears geocode cache and currentLocation.
   */
  const clearHistory = useCallback(() => {
    setCurrentLocation(null);
    setGeocodeCache([]);
    setError(null);
    setPermissionDenied(false);

    if (cfg.persistToStorage) {
      clearStorage(cfg.storageKey);
    }
  }, [cfg.persistToStorage, cfg.storageKey]);

  // ============================================
  // Context Value
  // ============================================

  const isLoading = isLocating || isReverseGeocoding;

  const contextValue = useMemo<LocationHistoryContextValue>(
    () => ({
      isSupported,
      isLocating,
      isReverseGeocoding,
      isLoading,
      error,
      permissionDenied,
      currentLocation,
      recentLocations: geocodeCache,   // Expose geocode cache as recentLocations
      requestLocation,                  // for API compatibility
      getLocationIfFresh,
      hasUserMoved,
      findNearbyCache,
      clearHistory,
    }),
    [
      isSupported,
      isLocating,
      isReverseGeocoding,
      isLoading,
      error,
      permissionDenied,
      currentLocation,
      geocodeCache,
      requestLocation,
      getLocationIfFresh,
      hasUserMoved,
      findNearbyCache,
      clearHistory,
    ]
  );

  return (
    <LocationHistoryContext.Provider value={contextValue}>
      {children}
    </LocationHistoryContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useLocationHistoryContext = (): LocationHistoryContextValue => {
  return useContext(LocationHistoryContext);
};